import {IAnyObject} from "../../common/SimpleInterface";
import {firstKey, isArray, isNativeObject, isNumeric, isString, stripHTML} from "../../common/SiteHelper";
import {IPointer} from "../../../tsx/component/form/model/FormModel";
import {preparedPointer} from "../../../tsx/component/form/helper/PointerHelper";


interface ILineError {
    itemID: string;
    fieldID: string;
    key:IPointer
    errors:string[];
}

interface ILineErrors {
    [key: string]: ILineError;
}

export default class ApiError
{
    status: number;
    code:string;
    message:string;
    lineErrors: ILineErrors;
    info: IAnyObject;


    constructor(status: number = 0,code:string = "", message:string = "",  lineErrors: ILineErrors = {},  info: IAnyObject = {})
    {
        this.status = status;
        this.code = code;
        this.message = message;
        this.lineErrors = lineErrors;
        this.info = info;
    }
}

export const determineLineErrors = (dataErrors:IAnyObject):ILineErrors =>
{
    const lineErrors:ILineErrors = {};

    const determineLineMessages = (formLineErrors:any):string[] => {
        if(!isArray(formLineErrors)){
            formLineErrors = [formLineErrors];
        }
        const lineMessages:string[] = [];
        for(let i = 0; i < formLineErrors.length; i ++){
            let errorLine = formLineErrors[i];
            let lineMessage:string|null = null;
            if(isNativeObject(errorLine)){
                if(isString(errorLine.message)){
                    lineMessage = errorLine.message;
                } else if(isString(errorLine.code)){
                    lineMessage = errorLine.code;
                }
            } else if(isString(errorLine)){
                lineMessage = errorLine;
            }
            if(lineMessage){
                lineMessages.push(stripHTML(lineMessage));
            }
        }
        return lineMessages;
    }

    for(let lineID in dataErrors)
    {
        if(!['line', 'object', 'array'].includes(lineID))
        {
            const lineMessages = determineLineMessages(dataErrors[lineID]);
            if(lineMessages.length)
            {
                const key = preparedPointer(lineID);
                const lineError:ILineError = {
                    fieldID: key.fieldID,
                    itemID: key.itemID!,
                    key: key,
                    errors: lineMessages
                }
                lineErrors[lineError.itemID] = lineError;
            }
        }
    }

    const determineNestedLineErrors = (fromObject:IAnyObject):ILineError[] => {
        const nestedLineErrors:ILineError[] = [];
        if(fromObject.line){
            for(let lineID in fromObject.line){
                const lineMessages = determineLineMessages(fromObject.line[lineID]);
                if(lineMessages.length)
                {
                    const key = preparedPointer(lineID);
                    const lineError:ILineError = {
                        fieldID: key.fieldID,
                        itemID: key.itemID!,
                        key: key,
                        errors: lineMessages
                    }
                    nestedLineErrors.push(lineError);
                }
            }
        }
        if(isNativeObject(fromObject.object)){
            for(let objectID in fromObject.object){
                const childObject = fromObject.object[objectID];
                if(isNativeObject(childObject)){
                    const childLineErrors:ILineError[] = determineNestedLineErrors(childObject);
                    for(const childLineError of childLineErrors)
                    {
                        const isNoLine = childLineError.fieldID === "NO_LINE" && !childLineError.key.childPointer;
                        const key = preparedPointer({
                            fieldID: objectID,
                            childPointer: !isNoLine ? childLineError.key : null
                        })
                        childLineError.key = key
                        childLineError.itemID = key.itemID!;
                        if(isNoLine){
                            childLineError.fieldID = key.fieldID;
                        }
                        nestedLineErrors.push(childLineError);
                    }
                }
            }
        }
        if(isNativeObject(fromObject.array)){
            for(let arrayID in fromObject.array){
                const childArray = fromObject.array[arrayID];
                if(isNativeObject(childArray) || isArray(childArray)){
                    for(let arrayIndex in childArray){
                        const arrayObject = childArray[arrayIndex];
                        if(isNumeric(arrayIndex) && isNativeObject(arrayObject)){
                            const childLineErrors:ILineError[] = determineNestedLineErrors(arrayObject);
                            for(const childLineError of childLineErrors)
                            {
                                const isNoLine = childLineError.fieldID === "NO_LINE" && !childLineError.key.childPointer;
                                const key = preparedPointer({
                                    fieldID: arrayID,
                                    index: parseInt(arrayIndex),
                                    childPointer: !isNoLine ? childLineError.key : null
                                })
                                childLineError.key = key
                                childLineError.itemID = key.itemID!;
                                if(isNoLine){
                                    childLineError.fieldID = key.fieldID;
                                }
                                nestedLineErrors.push(childLineError);
                            }
                        }
                    }
                }
            }
        }
        return nestedLineErrors;
    }

    const nestedLineErrors = determineNestedLineErrors(dataErrors);
    for(const nestedLineError of nestedLineErrors)
    {
        lineErrors[nestedLineError.itemID] = nestedLineError;
    }

    return lineErrors;
}

export const determineApiError = (response: any, required:boolean = false, defaultErrorCode:number = 0): ApiError | null => {

    if(!isNativeObject(response)){
        if(required){
            response = {};
        } else {
            return null;
        }
    }

    let status:number|null = null;
    let code = "";
    let message = "";
    let dataErrors:IAnyObject = {};
    let info:IAnyObject = {};
    const data = isNativeObject(response.data) ? response.data : {};

    if(isNativeObject(response.error_data, true) && isNativeObject(response.errors, true)){
        code = firstKey(response.error_data) ?? "";
        if(response.errors[code]){
            const data = isNativeObject(response.error_data[code]) ? response.error_data[code] : {};
            status = data.status && !isNaN(data.status) ? parseInt(data.status) : null;
            message = isArray(response.errors[code]) && response.errors[code].length ? response.errors[code][0] : "";
            dataErrors = isNativeObject(data.errors) ? data.errors : {};
            info = isNativeObject(data.info) ? data.info : {};
        }
    } else if(Object.keys(data).length) {

        status = data.status && !isNaN(data.status) ? parseInt(data.status) : null;
        code = isString(response.code) ? response.code : "";
        message = isString(response.message) ? response.message : "";
        dataErrors = isNativeObject(data.errors) ? data.errors : {};
        info = isNativeObject(data.info) ? data.info : {};
    } else if(response.fetch_response){
        status = response.fetch_response.status && !isNaN(response.fetch_response.status) ? parseInt(response.fetch_response.status) : null;
        message = isString(response.fetch_response.statusText) && response.fetch_response.statusText ? response.fetch_response.statusText : (isString(response.title) ? response.title : "");
    }



    if(status === null){
        if(!required){
            return null;
        }
        status = defaultErrorCode;
    }

    if(status >= 200 && status < 300) {
        if(!required){
            return null;
        }
        status = defaultErrorCode;
    }

    const apiError = new ApiError();
    apiError.status = status;
    apiError.code = code;
    const errorCodeParts = apiError.code.split(" ");
    if(errorCodeParts.length){
        apiError.code = errorCodeParts[errorCodeParts.length - 1];
    }
    apiError.message = message ? message : (apiError.code ? apiError.code : "Unknown Error [3]");
    apiError.lineErrors= determineLineErrors(dataErrors);
    apiError.info = info;
    return apiError;
}

export const invalidDataError = (message?:string): ApiError => {
    return new ApiError(400, "invalid_data", message ?? "Invalid Data");
}